import { BarChart3, BellRing, Briefcase, CreditCard, Flag, LineChart, LogOut, Shield, SlidersHorizontal, Target, Users, X } from 'lucide-react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth.tsx';
import { useNotifications } from '../lib/notifications.tsx';
import { Avatar } from './Avatar.tsx';

interface DrawerProps {
    open: boolean;
    onClose: () => void;
}

/* L'entrée active en LAITON, comme la puce active du mockup : c'est la
 * couleur du « vous êtes ici », le vert primaire restant celui de l'action. */
const linkClass = ({ isActive }: { isActive: boolean }): string =>
    `flex items-center gap-3 rounded-field px-4 py-3 tap-target transition-colors ${
        isActive ? 'bg-accent text-accent-content font-semibold' : 'hover:bg-base-300'
    }`;

export function Drawer({ open, onClose }: DrawerProps) {
    const { user, logout, isAdmin } = useAuth();
    const { nativeEnabled, setNativeEnabled, nativeSupported, nativePermission } = useNotifications();
    const navigate = useNavigate();

    const handleLogout = async (): Promise<void> => {
        await logout();
        onClose();
        void navigate('/connexion');
    };

    return (
        <>
            {/* Voile : ferme au tap, et n'existe pas pour les lecteurs d'écran. */}
            <div
                className={`fixed inset-0 z-40 bg-black/60 transition-opacity md:hidden ${
                    open ? 'opacity-100' : 'pointer-events-none opacity-0'
                }`}
                onClick={onClose}
                aria-hidden="true"
            />

            {/* `text-base-content` est explicite : sur l'accueil, le tiroir
                vit dans l'enveloppe au fond fairway, dont il héritait le
                texte BLANC — blanc sur papier clair, illisible. Un panneau
                opaque doit porter sa propre encre, pas celle du décor. */}
            <aside
                className={`fixed inset-y-0 left-0 z-50 flex w-72 flex-col bg-base-200 text-base-content shadow-xl transition-transform duration-200 ${
                    open ? 'translate-x-0' : '-translate-x-full'
                } md:translate-x-0`}
                aria-label="Navigation principale"
            >
                <div className="safe-top flex items-center justify-between px-4 py-4">
                    {/* Même geste que dans la barre supérieure — qui, elle,
                        disparaît au-delà de md : sur écran large, ce titre est
                        le seul « retour à l'accueil » évident. */}
                    <Link to="/" onClick={onClose} className="flex items-center gap-2">
                        <span className="text-2xl" aria-hidden="true">⛳</span>
                        <span className="text-lg font-bold tracking-tight">Golf</span>
                    </Link>
                    <button
                        type="button"
                        className="btn btn-ghost btn-square md:hidden"
                        onClick={onClose}
                        aria-label="Fermer le menu"
                    >
                        <X size={22} />
                    </button>
                </div>

                <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-2">
                    <NavLink to="/" end className={linkClass} onClick={onClose}>
                        <BarChart3 size={20} /> Tableau de bord
                    </NavLink>
                    {/* L'ordre du tiroir suit celui d'une journée de golf : on
                        joue, puis on s'entraîne à ce qui a manqué, puis on
                        regarde ce que ça donne. Les trois entrées du haut sont
                        celles qu'on ouvre sur le terrain, gantée, sans lire :
                        elles doivent tomber sous le pouce en premier.

                        Parcours et Entraînement sont au même rang — ce sont les
                        deux façons de jouer au golf, aucune n'est un sous-cas
                        de l'autre. */}
                    <NavLink to="/parcours" className={linkClass} onClick={onClose}>
                        <Flag size={20} /> Parcours
                    </NavLink>
                    <NavLink to="/entrainement" className={linkClass} onClick={onClose}>
                        <Target size={20} /> Entraînement
                    </NavLink>
                    {/* Statistiques ferme le trio : c'est le REGARD sur les
                        deux au-dessus, jamais une façon de jouer. Les parcours
                        joués n'ont plus d'entrée à eux — ils vivent au bas de
                        l'onglet Parcours de cette page, avec l'historique des
                        séances au bas de l'onglet Entraînement. Deux portes
                        vers la même chose, c'est une porte de trop dans un
                        menu qu'on ouvre d'une main en marchant. */}
                    <NavLink to="/statistiques" className={linkClass} onClick={onClose}>
                        <LineChart size={20} /> Statistiques
                    </NavLink>

                    {/* Puis ce qui se règle À LA MAISON, le soir : on ajoute un
                        partenaire ou on pèse un club assis, jamais debout sur
                        un green. Séparé du trio du dessus par une ligne, parce
                        que ce sont deux usages différents de l'application et
                        non deux rangs d'importance. */}
                    <div className="my-2 border-t border-base-300" />
                    {/* Les partenaires ne servent qu'au jeu à plusieurs, mais
                        se gèrent en dehors d'une partie. Enterrés en bas de
                        l'écran Putting, ils étaient introuvables. */}
                    <NavLink to="/amis" className={linkClass} onClick={onClose}>
                        <Users size={20} /> Mes partenaires
                    </NavLink>
                    {/* Une seule entrée pour tout l'équipement. L'inventaire
                        et les compositions de sac sont deux tables distinctes
                        en base — parce qu'un joueur possède plus de clubs
                        qu'il n'en emporte — mais c'est un détail de stockage :
                        pour lui, c'est le même geste et le même écran. */}
                    <NavLink to="/materiel" className={linkClass} onClick={onClose}>
                        <Briefcase size={20} /> Mon matériel
                    </NavLink>
                    {/* La licence est dans ce groupe et non dans celui du haut,
                        alors qu'elle se montre au départ du premier trou. C'est
                        une pièce d'identité, pas une façon de jouer : elle se
                        remplit à la maison comme le sac, et se sort une fois. */}
                    <NavLink to="/licence" className={linkClass} onClick={onClose}>
                        <CreditCard size={20} /> Licence
                    </NavLink>

                    {/* Une seule entrée pour tout l'administratif. Le tiroir
                        est l'écran qu'on ouvre d'une main en marchant : trois
                        lignes de back-office y noyaient les quatre entrées qui
                        servent vraiment. Les sous-sections vivent dans la page
                        /admin, sous ses propres onglets.

                        Le back-office n'a toujours pas d'adresse à lui, et
                        masquer l'entrée reste du confort d'interface : le
                        contrôle réel est fait par le serveur sur chaque route
                        /api/admin. */}
                    {isAdmin && (
                        <NavLink to="/admin" className={linkClass} onClick={onClose}>
                            <Shield size={20} /> Admin
                        </NavLink>
                    )}
                </nav>

                {/* ── Réglages terrain, accessibles sans quitter l'écran ───── */}
                <div className="space-y-1 border-t border-base-300 px-3 py-3">
                    <div className="px-1 pb-1 text-xs font-semibold uppercase tracking-wider opacity-50">
                        Réglages
                    </div>

                    {/* Notifications du système.
                        Réglage LOCAL, comme la permission dont il dépend :
                        l'accepter sur son téléphone ne dit rien de ce qu'on
                        veut sur l'ordinateur du bureau. Une permission refusée
                        ne se redemande pas — le navigateur s'en souvient — d'où
                        le message qui renvoie vers ses réglages plutôt qu'un
                        interrupteur qui ne ferait rien. */}
                    <label className="flex cursor-pointer items-center gap-3 rounded-field px-3 py-2.5 hover:bg-base-300">
                        <BellRing size={20} />
                        <span className="flex-1 text-sm">
                            Notifications
                            {!nativeSupported && (
                                <span className="block text-xs opacity-60">Non gérées par ce navigateur</span>
                            )}
                            {nativeSupported && nativePermission === 'denied' && (
                                <span className="block text-xs opacity-60">
                                    Bloquées : à réautoriser dans les réglages du navigateur
                                </span>
                            )}
                            {nativeSupported && nativePermission !== 'denied' && (
                                <span className="block text-xs opacity-60">
                                    Bannières du téléphone, application ouverte
                                </span>
                            )}
                        </span>
                        <input
                            type="checkbox"
                            className="toggle toggle-primary"
                            checked={nativeEnabled}
                            disabled={!nativeSupported || nativePermission === 'denied'}
                            onChange={(e) => void setNativeEnabled(e.target.checked)}
                        />
                    </label>

                    {/* Vibrations et écran toujours allumé ne sont plus ici.
                        Ce sont des réglages qu'on pose une fois et qu'on oublie ;
                        le tiroir est un menu de NAVIGATION, ils y occupaient deux
                        rangs sur le chemin de tous les jours. Ils restent dans
                        les préférences, où l'on va justement pour régler. */}
                </div>

                <div className="safe-bottom border-t border-base-300 px-3 py-3">
                    {/* Le bloc identité mène aux préférences : c'est là qu'on
                        va les chercher instinctivement, et cela évite une
                        cinquième ligne dans un menu qu'on veut court. */}
                    <NavLink
                        to="/preferences"
                        onClick={onClose}
                        className={({ isActive }) =>
                            `flex w-full items-center gap-3 rounded-field px-2 py-2 text-left transition-colors ${
                                isActive ? 'bg-base-300' : 'hover:bg-base-300'
                            }`}
                    >
                        <Avatar
                            name={user?.displayName ?? ''}
                            email={user?.email}
                            avatarUrl={user?.avatarUrl}
                            size={40}
                            handicap={user?.handicapIndex ?? null}
                        />
                        <span className="min-w-0 flex-1">
                            <span className="block truncate text-sm font-medium">{user?.displayName}</span>
                            <span className="block truncate text-xs opacity-60">Préférences</span>
                        </span>
                        <SlidersHorizontal size={18} className="shrink-0 opacity-50" />
                    </NavLink>
                    <button
                        type="button"
                        className="btn btn-ghost btn-block justify-start gap-3"
                        onClick={() => void handleLogout()}
                    >
                        <LogOut size={20} /> Se déconnecter
                    </button>
                </div>
            </aside>
        </>
    );
}
